import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Eye, Lock, Clock, Globe, Copy, Trash2, ArrowLeft, Pencil, BarChart3 } from 'lucide-react';
import { useWorkspaceId } from '../hooks/useWorkspace';
import {
  listPortfolios,
  deletePortfolio,
  type PortfolioSummary,
  type PortfolioAccessMode,
} from '../api/portfolios';
import { listSets } from '../api/sets';
import { Logo } from '../components/Logo';
import { CreatePortfolioModal } from '../components/portfolios/CreatePortfolioModal';
import { PortfolioStatsPanel } from '../components/portfolios/PortfolioStatsPanel';
import { timeAgo } from '../utils/timeAgo';

// Portfolios — list + create. Inga edit-flöden i v1 (delete + re-create funkar).
// Edit kan läggas till i v2 om Henrik begär.

export default function Portfolios() {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();
  const [createOpen, setCreateOpen] = useState(false);
  // Edit-state: när satt visas modalen i edit-mode för detta portfolio
  const [editingPortfolio, setEditingPortfolio] = useState<PortfolioSummary | null>(null);

  const portfoliosQuery = useQuery({
    queryKey: ['portfolios', workspaceId],
    queryFn: () => {
      if (!workspaceId) throw new Error('no workspace');
      return listPortfolios(workspaceId);
    },
    enabled: !!workspaceId,
  });

  const setsQuery = useQuery({
    queryKey: ['sets', workspaceId],
    queryFn: () => {
      if (!workspaceId) throw new Error('no workspace');
      return listSets(workspaceId);
    },
    enabled: !!workspaceId,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePortfolio(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolios', workspaceId] });
    },
  });

  const portfolios = portfoliosQuery.data ?? [];
  const sets = setsQuery.data ?? [];

  return (
    <div className="min-h-screen min-h-[100dvh]">
      {/* Top nav — same shell as Hub */}
      <nav className="sticky top-0 z-50 flex items-center gap-3 sm:gap-5 px-5 sm:px-10 py-3.5 bg-loupe-bg/85 backdrop-blur-xl border-b border-loupe-border">
        <Link to="/" className="text-loupe-text-3 hover:text-loupe-text text-xs uppercase tracking-[0.1em] py-2 inline-flex items-center gap-1.5 flex-shrink-0">
          <ArrowLeft className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Hub</span>
        </Link>
        <Link to="/" className="flex-shrink-0" aria-label="Vuelette home">
          <Logo size="sm" color="#ededed" />
        </Link>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          disabled={!workspaceId || sets.length === 0}
          className="px-4 py-2.5 bg-white text-black border-none rounded-full text-xs font-semibold tracking-wider uppercase cursor-pointer transition-transform min-h-[40px] inline-flex items-center gap-2 whitespace-nowrap hover:scale-[1.03] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
          <span className="hidden sm:inline">New portfolio</span>
          <span className="sm:hidden">New</span>
        </button>
      </nav>

      <div className="px-5 sm:px-10 pt-12 pb-32 max-w-[1280px] mx-auto">
        {/* Page header */}
        <header className="mb-10">
          <div className="text-[11px] text-loupe-text-3 tracking-[0.18em] uppercase mb-2">
            Portfolios
          </div>
          <h1 className="font-display font-light text-5xl sm:text-6xl tracking-[-0.04em] leading-[0.95] mb-3">
            Living links <em className="italic font-normal text-loupe-text-2">you can share</em>
          </h1>
          <p className="text-loupe-text-2 max-w-[60ch]">
            Build a public link from one or two sets. The link stays alive — when you
            update the set, the portfolio updates too.
          </p>
        </header>

        {/* List */}
        {portfoliosQuery.isLoading ? (
          <PortfoliosSkeleton />
        ) : sets.length === 0 ? (
          <EmptyNoSets />
        ) : portfolios.length === 0 ? (
          <EmptyNoPortfolios onCreate={() => setCreateOpen(true)} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {portfolios.map((p) => (
              <PortfolioCard
                key={p.id}
                portfolio={p}
                onDelete={() => {
                  if (window.confirm(`Delete "${p.name}"? The link will stop working.`)) {
                    deleteMutation.mutate(p.id);
                  }
                }}
                onEdit={() => setEditingPortfolio(p)}
                onOpen={() => window.open(`/p/${p.slug}`, '_blank', 'noopener')}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create modal */}
      {createOpen && workspaceId ? (
        <CreatePortfolioModal
          workspaceId={workspaceId}
          sets={sets}
          onClose={() => setCreateOpen(false)}
          onSaved={(p) => {
            setCreateOpen(false);
            queryClient.invalidateQueries({ queryKey: ['portfolios', workspaceId] });
            // Show created portfolio in new tab
            window.open(`/p/${p.slug}`, '_blank', 'noopener');
          }}
        />
      ) : null}

      {/* Edit modal */}
      {editingPortfolio && workspaceId ? (
        <CreatePortfolioModal
          workspaceId={workspaceId}
          sets={sets}
          portfolio={editingPortfolio}
          onClose={() => setEditingPortfolio(null)}
          onSaved={() => {
            setEditingPortfolio(null);
            queryClient.invalidateQueries({ queryKey: ['portfolios', workspaceId] });
            queryClient.invalidateQueries({ queryKey: ['public-portfolio'] });
          }}
        />
      ) : null}
    </div>
  );
}

// ─── Portfolio card ──────────────────────────────────────────────────────

function PortfolioCard({
  portfolio,
  onDelete,
  onEdit,
  onOpen,
}: {
  portfolio: PortfolioSummary;
  onDelete: () => void;
  onEdit: () => void;
  onOpen: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const url = `${window.location.origin}/p/${portfolio.slug}`;

  function copyLink() {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="p-6 bg-loupe-bg-2 border border-loupe-border rounded-2xl flex flex-col gap-4 hover:border-loupe-border-2 transition-colors group">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-xl font-medium tracking-tight mb-1 truncate">
            {portfolio.name}
          </h3>
          <div className="text-[11px] text-loupe-text-3 tracking-wider uppercase tabular-nums">
            {portfolio.lastViewedAt
              ? `Viewed ${timeAgo(portfolio.lastViewedAt)}`
              : `Created ${timeAgo(portfolio.createdAt)}`}
          </div>
        </div>
        <AccessModeBadge mode={portfolio.accessMode} expiresAt={portfolio.expiresAt} />
      </div>

      {/* URL */}
      <div className="flex items-center gap-2 px-3 py-2.5 bg-loupe-bg-3 border border-loupe-border rounded-lg text-xs text-loupe-text-2 font-mono">
        <span className="truncate flex-1">{url}</span>
        <button
          type="button"
          onClick={copyLink}
          title="Copy link"
          className="text-loupe-text-3 hover:text-loupe-text transition-colors p-1 -m-1"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
        {copied ? (
          <span className="text-emerald-400 text-[10px] uppercase tracking-wider">Copied</span>
        ) : null}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 mt-auto">
        <button
          type="button"
          onClick={onOpen}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-black rounded-full text-xs font-semibold tracking-wider uppercase min-h-[36px] hover:scale-[1.02] transition-transform"
        >
          <Eye className="w-3.5 h-3.5" />
          Open
        </button>
        <button
          type="button"
          onClick={onEdit}
          className="px-3 py-2.5 bg-transparent border border-loupe-border-2 text-loupe-text-2 hover:text-loupe-text hover:border-loupe-text-3 rounded-full text-xs uppercase tracking-wider min-h-[36px] transition-colors inline-flex items-center gap-1.5"
          aria-label="Edit portfolio"
        >
          <Pencil className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Edit</span>
        </button>
        <button
          type="button"
          onClick={() => setStatsOpen((v) => !v)}
          className={`px-3 py-2.5 bg-transparent border rounded-full text-xs uppercase tracking-wider min-h-[36px] transition-colors inline-flex items-center gap-1.5 ${
            statsOpen
              ? 'border-loupe-text-2 text-loupe-text'
              : 'border-loupe-border-2 text-loupe-text-2 hover:text-loupe-text hover:border-loupe-text-3'
          }`}
          aria-label="Show stats"
          aria-expanded={statsOpen}
        >
          <BarChart3 className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Stats</span>
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="px-3 py-2.5 bg-transparent border border-loupe-border-2 text-loupe-text-3 hover:text-michelle hover:border-michelle/40 rounded-full text-xs uppercase tracking-wider min-h-[36px] transition-colors"
          aria-label="Delete portfolio"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Stats panel — endast renderad när öppen (lazy load) */}
      {statsOpen ? <PortfolioStatsPanel portfolioId={portfolio.id} /> : null}
    </div>
  );
}

function AccessModeBadge({
  mode,
  expiresAt,
}: {
  mode: PortfolioAccessMode;
  expiresAt: string | null;
}) {
  const config = {
    public: { label: 'Public', icon: Globe, color: 'text-loupe-text-2 border-loupe-border-2' },
    password: { label: 'PIN', icon: Lock, color: 'text-michelle border-michelle/40' },
    expiring: { label: 'Expiring', icon: Clock, color: 'text-amber-300 border-amber-300/40' },
  } as const;
  const c = config[mode];
  const Icon = c.icon;

  let title: string | undefined;
  if (mode === 'expiring' && expiresAt) {
    title = `Expires ${new Date(expiresAt).toLocaleDateString()}`;
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 border rounded-full text-[10px] uppercase tracking-wider whitespace-nowrap ${c.color}`}
      title={title}
    >
      <Icon className="w-3 h-3" />
      {c.label}
    </span>
  );
}

// ─── Skeletons / empty states ────────────────────────────────────────────

function PortfoliosSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="p-6 bg-loupe-bg-2 border border-loupe-border rounded-2xl animate-pulse h-[180px]"
        />
      ))}
    </div>
  );
}

function EmptyNoSets() {
  return (
    <div className="rounded-2xl border border-dashed border-loupe-border-2 p-12 text-center">
      <div className="font-display text-2xl mb-2">Create a set first</div>
      <p className="text-loupe-text-2 text-sm mb-5 max-w-[44ch] mx-auto">
        Portfolios are built on top of curated sets. Create a set with your best work,
        then come back here to publish it as a shareable portfolio.
      </p>
      <Link
        to="/"
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-black text-xs font-semibold uppercase tracking-wider min-h-[40px] hover:scale-[1.03] transition-transform"
      >
        Back to hub
      </Link>
    </div>
  );
}

function EmptyNoPortfolios({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="rounded-2xl border border-dashed border-loupe-border-2 p-12 text-center">
      <div className="font-display text-2xl mb-2">No portfolios yet</div>
      <p className="text-loupe-text-2 text-sm mb-5 max-w-[44ch] mx-auto">
        A portfolio is a public, shareable link built from one or two of your sets.
        Pick a set, add your meta, send the link.
      </p>
      <button
        type="button"
        onClick={onCreate}
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-black text-xs font-semibold uppercase tracking-wider min-h-[40px] hover:scale-[1.03] transition-transform"
      >
        <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
        Create your first portfolio
      </button>
    </div>
  );
}
